import { showToast } from '@/helper/toast.helper';

import { GetPosterCodeResponseData } from '@/store/slices/surveySetManagementSlice/surveySetManagementSlice';

import {
  CreateSurveySetDto,
  CreateSurveySetResponseDto,
  GetAllSurveySetsResponseDto,
  GetSurveySetByIdResponseDto,
  GetSurveySetsStatsResponseDto,
  SurveySetBySitesResponse,
  SurveySetFilters,
  UpdateSurveySetBody,
  UpdateSurveySetResponseDto,
} from '@/types/surveySet.types';
import api from '../instance';

export const API_URL = `/api/v1`;

export const surveySetManagementService = {
  getSurveySetsStats: async () => {
    try {
      const res = await api.projectApi.get<GetSurveySetsStatsResponseDto>(`/surveyset/count`, {
        headers: {
          'x-show-loader': 'true',
          'X-Mm-Service': 'mm-content-mgmt-svc',
        },
      });

      return res.data;
    } catch (error: unknown) {
      if (error instanceof Error) throw error;
      throw new Error('Error fetching surveys');
    }
  },

  createSurveySet: async (payload: CreateSurveySetDto) => {
    try {
      const res = await api.projectApi.post<CreateSurveySetResponseDto>(`/surveyset`, payload, {
        headers: {
          'x-show-loader': 'true',
          'X-Mm-Service': 'mm-content-mgmt-svc',
        },
      });
      return res.data;
    } catch (error: unknown) {
      if (error instanceof Error) throw error;
      throw new Error('Error creating survey set');
    }
  },
  getAllSurveySets: async (filters: SurveySetFilters) => {
    try {
      const res = await api.projectApi.get<GetAllSurveySetsResponseDto>(`/surveyset`, {
        params: filters,
        headers: {
          'x-show-loader': 'true',
          'X-Mm-Service': 'mm-content-mgmt-svc',
        },
      });
      const roleInfoStr = localStorage.getItem('roleInfo');
      const roleName = roleInfoStr ? JSON.parse(roleInfoStr).name : undefined;

      if (roleName === 'SUPER_ADMIN' || roleName === 'ADMIN') return res.data;

      const projectRoles: Record<string, string> = {};
      res.data.data.forEach((surveyset) => {
        projectRoles[surveyset.site.project.id] = surveyset.roleInfo.name;
      });
      localStorage.setItem('projectRoles', JSON.stringify(projectRoles));

      return res.data;
    } catch (error) {
      if (error instanceof Error) throw error;
      throw new Error('Error get all survey sets');
    }
  },
  getSurveySetById: async (id: string) => {
    try {
      const res = await api.projectApi.get<GetSurveySetByIdResponseDto>(`/surveyset/${id}`, {
        headers: {
          'x-show-loader': 'true',
          'X-Mm-Service': 'mm-content-mgmt-svc',
        },
      });
      return res.data;
    } catch (error: unknown) {
      if (error instanceof Error) throw error;
      throw new Error('Error fetching survey set');
    }
  },

  getSurveySetBySiteId: async (id: number) => {
    try {
      const res = await api.projectApi.get<SurveySetBySitesResponse>(`/surveyset/site/${id}`, {
        headers: {
          'x-show-loader': 'true',
          'X-Mm-Service': 'mm-content-mgmt-svc',
        },
      });
      return res.data;
    } catch (error: unknown) {
      if (error instanceof Error) throw error;
      throw new Error('Error fetching survey set');
    }
  },

  updateSurveySet: async (id: string, payload: UpdateSurveySetBody) => {
    try {
      const res = await api.projectApi.put<UpdateSurveySetResponseDto>(
        `/surveyset/${id}`,
        payload,
        {
          headers: {
            'x-show-loader': 'true',
            'X-Mm-Service': 'mm-content-mgmt-svc',
          },
        },
      );
      return res.data;
    } catch (error: unknown) {
      if (error instanceof Error) throw error;
      throw new Error('Error updating survey set');
    }
  },
  updateSurveySetStatus: async (id: string, data: UpdateSurveySetBody) => {
    try {
      const res = await api.projectApi.put<UpdateSurveySetResponseDto>(`/surveyset/${id}`, data, {
        headers: {
          // No 'x-show-loader' header - silent update
          'X-Mm-Service': 'mm-content-mgmt-svc',
        },
      });
      return res.data;
    } catch (error: unknown) {
      if (error instanceof Error) throw error;
      throw new Error('Error updating survey set status');
    }
  },
  getSurveySetUsersCount: async (params = {}) => {
    try {
      const res = await api.projectApi.get<{ data: { count: number } }>(`/surveyset/users/count`, {
        params,
        headers: {
          'x-show-loader': 'true',
          'X-Mm-Service': 'mm-content-mgmt-svc',
        },
      });
      return res.data;
    } catch (error: any) {
      const msg = error?.message || 'Error updating survey set status';
      showToast.error(msg);
      throw new Error(msg);
    }
  },

  deleteSurveySet: async (id: string, deletedBy: number): Promise<CreateSurveySetResponseDto> => {
    try {
      const res = await api.projectApi.delete<CreateSurveySetResponseDto>(`/surveyset/${id}`, {
        params: {
          deletedBy,
        },
        headers: {
          'x-show-loader': 'true',
          'X-Mm-Service': 'mm-content-mgmt-svc',
        },
      });
      return res.data;
    } catch (error: unknown) {
      if (error instanceof Error) throw error;
      throw new Error('Error deleting project');
    }
  },
  exportSurveySets: async () => {
    try {
      const res = await api.projectApi.get('/surveyset/bulk-export', {
        responseType: 'blob',
      });
      return res.data;
    } catch (error) {
      if (error instanceof Error) throw error;
      throw new Error('Error exporting survey sets');
    }
  },

  exportSurveyResponse: async ({
    surveySetId,
    surveyId,
  }: {
    surveySetId?: number;
    surveyId?: number | null;
  }) => {
    try {
      const res = await api.projectApi.get('/surveyset/survey/responses/export', {
        params: {
          surveySetId,
          surveyId: surveyId ?? '', // force empty string
        },
        paramsSerializer: (params) =>
          Object.entries(params)
            .map(([key, value]) => `${key}=${value ?? ''}`)
            .join('&'),
        responseType: 'blob',
      });

      return res.data;
    } catch (error) {
      if (error instanceof Error) throw error;
      throw new Error('Error exporting survey sets');
    }
  },

  updateSurveyStatusInSet: async (
    surveySetId: string | number,
    surveyId: string | number,
    status: boolean,
  ) => {
    try {
      const res = await api.projectApi.patch(
        `/surveyset/${surveySetId}/survey/${surveyId}/status`,
        { status },
        {
          headers: {
            'X-Mm-Service': 'mm-content-mgmt-svc',
          },
        },
      );
      return res.data;
    } catch (error: unknown) {
      if (error instanceof Error) throw error;
      throw new Error('Error updating survey status in survey set');
    }
  },

  deleteSurveyFromSet: async (surveySetId: string | number, surveyId: string | number) => {
    try {
      const res = await api.projectApi.delete(`/surveyset/${surveySetId}/survey/${surveyId}`, {
        headers: {
          'x-show-loader': 'true',
          'X-Mm-Service': 'mm-content-mgmt-svc',
        },
      });
      return res.data;
    } catch (error: unknown) {
      if (error instanceof Error) throw error;
      throw new Error('Error removing survey from survey set');
    }
  },

  getPosterCode: async (posterCode: number | string) => {
    try {
      const res = await api.projectApi.get<GetPosterCodeResponseData>(
        `/surveyset/by-poster-code/${posterCode}`,
        {
          headers: {
            'x-show-loader': 'true',
            'X-Mm-Service': 'mm-content-mgmt-svc',
          },
        },
      );
      return res.data;
    } catch (error: any) {
      const msg = error?.message || 'Error updating survey set status';
      showToast.error(msg);
      throw new Error(msg);
    }
  },
};
